<template>
    <div class="industry-sectors-modal">
        <el-dialog
            :title="$trans('web_profile.assign_industry_sectors')"
            :model-value="WebProfileStore.isEditIndustrySectorsModalOpen"
            :show-close="false"
            class="custom-modal"
            @open="initializeLocalData"
            @close="closeEditIndustrySectorsModal"
        >
            <span
                class="dialog-cancel-text"
                @click="closeEditIndustrySectorsModal"
            >
                {{ $trans("profile_dossier.cancel") }}
                <el-icon><Close /></el-icon>
            </span>
            <el-button type="primary" @click="saveChanges" :loading="loading">
                {{ $trans("web_profile.save") }}
            </el-button>

            <div class="table-container" v-loading="loading">
                <el-input
                    v-model="searchEmployment"
                    :placeholder="$trans('profile_dossier.search_placeholder')"
                    clearable
                    class="custom-search-input"
                >
                    <template #suffix>
                        <img
                            src="/media/images/search_gray.png"
                            alt="Search"
                            style="width: 16px; height: 16px; opacity: 0.6"
                        />
                    </template>
                </el-input>

                <EasyDataTable
                    v-if="!loading"
                    :headers="employmentHeaders"
                    :items="sortedItems"
                    :search-value="searchEmployment"
                    table-class-name="employment-table"
                    :rows-per-page="10"
                    :buttons-pagination="true"
                >
                    <template #header-period_info>
                        <div class="custom-table-header">
                            <span
                                @click.stop="changeSort('to')"
                                :class="{ active: sortBy === 'to' }"
                            >
                                {{ $trans("profile_dossier.from_to") }}
                                <SortIcons
                                    :columnKey="'to'"
                                    :sortBy="sortBy"
                                    :sortDesc="sortDesc"
                                />
                            </span>
                            <span
                                @click.stop="changeSort('duration_sort')"
                                :class="{ active: sortBy === 'duration_sort' }"
                            >
                                {{ $trans("profile_dossier.duration") }}
                                <SortIcons
                                    :columnKey="'duration_sort'"
                                    :sortBy="sortBy"
                                    :sortDesc="sortDesc"
                                />
                            </span>
                        </div>
                    </template>

                    <template #item-period_info="{ from, to, duration }">
                        <div class="cell-multiline">
                            <div>
                                {{ formatDate(from) }} –
                                {{
                                    to && to !== "-"
                                        ? formatDate(to)
                                        : $trans("profile_dossier.ongoing")
                                }}
                            </div>
                            <div>{{ duration }}</div>
                        </div>
                    </template>

                    <template #header-org_info>
                        <div class="custom-table-header">
                            <span
                                @click.stop="changeSort('organization_name')"
                                :class="{
                                    active: sortBy === 'organization_name',
                                }"
                            >
                                {{ $trans("profile_dossier.organization") }}
                                <SortIcons
                                    :columnKey="'organization_name'"
                                    :sortBy="sortBy"
                                    :sortDesc="sortDesc"
                                />
                            </span>
                            <span
                                @click.stop="changeSort('position_title')"
                                :class="{ active: sortBy === 'position_title' }"
                            >
                                {{ $trans("profile_dossier.position_project") }}
                                <SortIcons
                                    :columnKey="'position_title'"
                                    :sortBy="sortBy"
                                    :sortDesc="sortDesc"
                                />
                            </span>
                        </div>
                    </template>

                    <template
                        #item-org_info="{ organization_name, position_title }"
                    >
                        <div class="cell-multiline">
                            <div>{{ organization_name }}</div>
                            <div>{{ position_title }}</div>
                        </div>
                    </template>

                    <template #header-location_info>
                        <div class="custom-table-header">
                            <span
                                @click.stop="changeSort('city')"
                                :class="{ active: sortBy === 'city' }"
                            >
                                {{ $trans("profile_dossier.city") }}
                                <SortIcons
                                    :columnKey="'city'"
                                    :sortBy="sortBy"
                                    :sortDesc="sortDesc"
                                />
                            </span>
                            <span
                                @click.stop="changeSort('country')"
                                :class="{ active: sortBy === 'country' }"
                            >
                                {{ $trans("profile_dossier.country") }}
                                <SortIcons
                                    :columnKey="'country'"
                                    :sortBy="sortBy"
                                    :sortDesc="sortDesc"
                                />
                            </span>
                        </div>
                    </template>

                    <template #item-location_info="{ city, country }">
                        <div class="cell-multiline">
                            <div>{{ city }}</div>
                            <div>{{ country }}</div>
                        </div>
                    </template>

                    <template #item-job_description="{ job_description }">
                        <template v-if="stripHtml(job_description).trim()">
                            <div class="cell-content-wrapper">
                                <span class="text-content">
                                    {{
                                        stripHtml(
                                            job_description.slice(0, 50) + "..."
                                        )
                                    }}
                                </span>

                                <img
                                    src="/media/images/drupal_reference_icon.png"
                                    alt="Reference Icon"
                                    class="reference-icon"
                                    @click="
                                        openDescriptionModal(job_description)
                                    "
                                />
                            </div>
                        </template>
                    </template>

                    <template #header-sectors>
                        <div class="custom-table-header">
                            <span>{{
                                $trans("web_profile.industry_sectors")
                            }}</span>
                        </div>
                    </template>

                    <template #item-sectors="item">
                        <div class="sectors-select-container">
                            <el-tooltip
                                :content="getIndustryName(item.sector1_id)"
                                :popper-style="{ fontSize: '16px' }"
                                :disabled="
                                    !item.sector1_id ||
                                    !getIndustryName(item.sector1_id) ||
                                    getIndustryName(item.sector1_id).length <= 22
                                "
                                placement="top"
                                effect="light"
                            >
                                <el-select-v2
                                    :model-value="item.sector1_id"
                                    :options="industryOptions"
                                    @change="
                                        (val) =>
                                            handleSectorChange(item.id, 1, val)
                                    "
                                    :placeholder="
                                        $trans('web_profile.sector_1')
                                    "
                                    clearable
                                    filterable
                                    class="mb-2"
                                    :teleported="false"
                                    style="width: 100%"
                                >
                                    <template #default="{ item }">
                                        <el-tooltip
                                            :content="item.label"
                                            placement="right"
                                            effect="light"
                                            :open-delay="300"
                                            :disabled="!item.label || item.label.length <= 22"
                                            :popper-style="{ fontSize: '16px' }"
                                        >
                                            <span
                                                style="
                                                    display: block;
                                                    width: 100%;
                                                    overflow: hidden;
                                                    text-overflow: ellipsis;
                                                    white-space: nowrap;
                                                "
                                            >
                                                {{ item.label }}
                                            </span>
                                        </el-tooltip>
                                    </template>
                                </el-select-v2>
                            </el-tooltip>

                            <el-tooltip
                                :content="getIndustryName(item.sector2_id)"
                                :disabled="
                                    !item.sector2_id ||
                                    !getIndustryName(item.sector2_id) ||
                                    getIndustryName(item.sector2_id).length <= 22
                                "
                                placement="top"
                                effect="light"
                            >
                                <el-select-v2
                                    :model-value="item.sector2_id"
                                    :options="industryOptions"
                                    @change="
                                        (val) =>
                                            handleSectorChange(item.id, 2, val)
                                    "
                                    :placeholder="
                                        $trans('web_profile.sector_2')
                                    "
                                    clearable
                                    filterable
                                    :teleported="false"
                                    style="width: 100%"
                                >
                                    <template #default="{ item }">
                                        <el-tooltip
                                            :popper-style="{ fontSize: '16px' }"
                                            :content="item.label"
                                            placement="right"
                                            effect="light"
                                            :open-delay="300"
                                            :disabled="!item.label || item.label.length <= 22"
                                        >
                                            <span
                                                style="
                                                    display: block;
                                                    width: 100%;
                                                    overflow: hidden;
                                                    text-overflow: ellipsis;
                                                    white-space: nowrap;
                                                "
                                            >
                                                {{ item.label }}
                                            </span>
                                        </el-tooltip>
                                    </template>
                                </el-select-v2>
                            </el-tooltip>
                        </div>
                    </template>

                    <template #empty-message>
                        <div
                            style="
                                text-align: center;
                                padding: 20px;
                                color: #888;
                            "
                        >
                            {{ $trans("global.no_data_found") }}
                        </div>
                    </template>
                </EasyDataTable>
            </div>
        </el-dialog>

        <el-dialog
            :title="$trans('web_profile.project_summary')"
            v-model="descriptionDialogVisible"
            width="50%"
            append-to-body
            class="description-modal"
        >
            <div class="description-content">
                <p style="white-space: pre-wrap">
                    {{ stripHtml(currentDescription) }}
                </p>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { mapStores, mapState, mapActions } from "pinia";
import { useWebProfileStore } from "@/stores/WebProfileStore";
import {
    ElDialog,
    ElButton,
    ElSelectV2,
    ElInput,
    ElTooltip,
} from "element-plus";
import { Close } from "@element-plus/icons-vue";
import EasyDataTable from "vue3-easy-data-table";
import SortIcons from "./../../../ProfileDossier/Components/Tables/SortIcons.vue";
import axios from "axios";
import "vue3-easy-data-table/dist/style.css";

export default {
    name: "EditIndustrySectorsModal",
    components: {
        EasyDataTable,
        ElDialog,
        ElButton,
        ElSelectV2,
        ElInput,
        ElTooltip,
        SortIcons,
        Close,
    },
    data() {
        return {
            loading: false,
            localExperiences: [],
            allIndustrySectors: [],
            searchEmployment: "",
            descriptionDialogVisible: false,
            currentDescription: "",
            sortBy: "to",
            sortDesc: true,
            employmentHeaders: [
                {
                    text: this.$trans("web_profile.period_duration"),
                    value: "period_info",
                    sortable: true,
                    width: 140,
                },
                {
                    text: this.$trans("web_profile.organization_position"),
                    value: "org_info",
                    sortable: true,
                },
                {
                    text: this.$trans("web_profile.location"),
                    value: "location_info",
                    sortable: true,
                    width: 140,
                },
                {
                    text: this.$trans("web_profile.project_summary"),
                    value: "job_description",
                },
                {
                    text: this.$trans("web_profile.industry_sectors"),
                    value: "sectors",
                    width: 350,
                },
            ],
        };
    },
    computed: {
        ...mapStores(useWebProfileStore),
        ...mapState(useWebProfileStore, [
            "isEditIndustrySectorsModalOpen",
            "profile",
        ]),

        isOpen: {
            get() {
                return this.isEditIndustrySectorsModalOpen;
            },
            set(value) {
                if (!value) this.closeEditIndustrySectorsModal();
            },
        },
        industryOptions() {
            return this.allIndustrySectors.map((sector) => ({
                value: sector.id,
                label: sector.name || "",
            }));
        },

        sortedItems() {
            if (!this.sortBy) return this.localExperiences;
            const items = [...this.localExperiences];

            items.sort((a, b) => {
                let valA = a[this.sortBy];
                let valB = b[this.sortBy];

                if (this.sortBy === "from" || this.sortBy === "to") {
                    const today = new Date();
                    valA = !valA || valA === "-" ? today : new Date(valA);
                    valB = !valB || valB === "-" ? today : new Date(valB);
                }

                if (this.sortBy === "duration_sort") {
                    valA = Number(a.duration_sort ?? 0);
                    valB = Number(b.duration_sort ?? 0);
                }

                if (typeof valA === "string") valA = valA.toLowerCase();
                if (typeof valB === "string") valB = valB.toLowerCase();

                if (valA == null) return 1;
                if (valB == null) return -1;

                if (valA < valB) return this.sortDesc ? 1 : -1;
                if (valA > valB) return this.sortDesc ? -1 : 1;
                return 0;
            });
            return items;
        },
    },
    mounted() {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("open_modal") === "industry_sectors") {
            this.WebProfileStore.isEditIndustrySectorsModalOpen = true;
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    },
    methods: {
        ...mapActions(useWebProfileStore, [
            "closeEditIndustrySectorsModal",
            "saveIndustrySectorAssignments",
        ]),

        async initializeLocalData() {
            this.loading = true;
            this.localExperiences = [];
            this.allIndustrySectors = [];
            this.searchEmployment = "";

            try {
                const profileId = this.profile?.id;
                if (!profileId) throw new Error("Profile ID not found.");

                const [experiencesResponse, sectorsResponse] =
                    await Promise.all([
                        axios.get(
                            `/candidates/${profileId}/web-profile/experience-industry-sectors`
                        ),
                        axios.get(`/web-profile/${profileId}/industry-sectors`),
                    ]);

                const experiences =
                    experiencesResponse.data?.data ||
                    experiencesResponse.data ||
                    [];
                const sectors =
                    sectorsResponse.data?.data || sectorsResponse.data || [];

                this.allIndustrySectors = Object.freeze(sectors);

                this.localExperiences = experiences.map((exp) => ({
                    ...exp,
                    id: exp.id,
                    duration_sort: parseInt(exp.duration_in_month || 0, 10),
                    duration: exp.duration_web_profile || "",
                    period_info: `${this.formatDate(
                        exp.from
                    )} - ${this.formatDate(exp.to)}`,

                    org_info: `${exp.organization_name || ""} / ${
                        exp.position_title || ""
                    }`,
                    location_info: `${exp.city || ""}${
                        exp.city && exp.country ? ", " : ""
                    }${exp.country || ""}`,
                    job_description: this.stripHtml(
                        exp.short_job_description || exp.job_description || ""
                    ),
                    sector1_id: exp.industry_sectors?.[0]?.id ?? null,
                    sector2_id: exp.industry_sectors?.[1]?.id ?? null,
                    city: exp.city || "",
                    country: exp.country || "",
                }));
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                this.loading = false;
            }
        },

        handleSectorChange(itemId, sectorIndex, newValue) {
            const index = this.localExperiences.findIndex(
                (exp) => exp.id === itemId
            );
            if (index !== -1) {
                if (sectorIndex === 1) {
                    this.localExperiences[index].sector1_id = newValue;
                } else if (sectorIndex === 2) {
                    this.localExperiences[index].sector2_id = newValue;
                }
            }
        },

        changeSort(column) {
            if (this.sortBy === column) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortBy = column;
                this.sortDesc = false;
            }
        },

        openDescriptionModal(description) {
            this.currentDescription = description;
            this.descriptionDialogVisible = true;
        },

        async saveChanges() {
            this.$functions.showLoader();
            try {
                const assignments = this.localExperiences.map((exp) => {
                    const sectors = [];
                    if (exp.sector1_id)
                        sectors.push({ id: exp.sector1_id, type: "main" });
                    if (exp.sector2_id)
                        sectors.push({ id: exp.sector2_id, type: "detail" });

                    return {
                        experience_id: exp.id,
                        sectors: sectors,
                    };
                });

                await this.saveIndustrySectorAssignments(assignments);
                this.$message.success(
                    this.$trans("web_profile.industry_sectors_saved")
                );
                this.closeEditIndustrySectorsModal();
            } catch (error) {
                console.error("Error saving industry sectors:", error);
                this.$message.error(
                    this.$trans("web_profile.something_went_wrong")
                );
            } finally {
                this.loading = false;
            }
        },

        stripHtml(html) {
            if (!html) return "";
            const div = document.createElement("div");
            div.innerHTML = html;
            return div.textContent || div.innerText || "";
        },

        formatDate(dateString) {
            if (!dateString) return "Present";
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${String(date.getMonth() + 1).padStart(
                2,
                "0"
            )}/${date.getFullYear()}`;
        },
        getIndustryName(id) {
            if (!id) return "";
            const sector = this.industryOptions.find((s) => s.value === id);
            return sector ? sector.label : "";
        },
    },
};
</script>

<style scoped>
.industry-sectors-modal :deep(.el-dialog) {
    --el-dialog-width: 1400px;
    top: -100px !important;
}

.table-container {
    width: 100%;
    box-sizing: border-box;
    padding-bottom: 24px;
    padding-top: 10px;
    background-color: #ededed;
    font-family: "Barlow", sans-serif;
    padding-left: 14px;
    padding-right: 14px;
}

:deep(.el-dialog__header) {
    padding-bottom: 40px;
}

:deep(.el-input) {
    width: 40%;
}

:deep(.el-input__prefix) {
    width: 0 !important;
}

:deep(.el-input__wrapper) {
    border-radius: 0 !important;
    margin-bottom: 20px;
    height: 37px;
}

:deep(.el-input__wrapper.is-focus) {
    border: 2px solid #41abc4 !important;
    box-shadow: none !important;
}

.employment-table {
    width: 100%;
    --easy-table-header-font-size: 14px;
    --easy-table-body-row-font-size: 14px;
    --easy-table-body-row-hover-background-color: #fff;
    --easy-table-border: none;
}

:deep(.vue3-easy-data-table) {
    margin-bottom: unset !important;
    border: none !important;
    background-color: #ededed;
}

:deep(.vue3-easy-data-table__main) {
    background-color: #ededed;
    margin-bottom: unset !important;
}

:deep(.vue3-easy-data-table__main tr td) {
    height: 1px !important;
    background: #ffffff !important;
    padding: 0.5rem !important;
    line-height: 20px !important;
    border: 2px solid #ededed !important;
    font-size: 16px !important;
    font-weight: 700 !important;
}

:deep(.vue3-easy-data-table__main tr th) {
    border-top: 0;
    border-bottom: 0;
    background: rgb(237, 237, 237);
    padding: 0 0.5rem;
    color: #3c3c3c;
    font-style: normal;
    font-weight: 400;
    font-size: 16px !important;
    height: 24px;
    line-height: 24px;
    font-family: sans-serif;
    border-top: unset;
    border: 2px solid #ededed !important;
}

:deep(.vue3-easy-data-table__footer) {
    background-color: transparent !important;
    justify-content: center !important;
    display: flex !important;
}

:deep(.vue3-easy-data-table__body tr td:first-child) {
    background-color: #d2e1e5 !important;
    text-align: center !important;
}

:deep(.vue3-easy-data-table__body tr td:last-child) {
    background-color: #d2e1e5 !important;
}

:deep(.pagination__rows-per-page) {
    display: none !important;
}

:deep(.pagination__items-index) {
    display: none !important;
}

:deep(.buttons-pagination) {
    transform: scale(1.5);
    margin-top: 25px;
}

:deep(.buttons-pagination .item.button) {
    border-radius: 0;
}

:deep(.buttons-pagination .item.button.active) {
    color: #fff;
    background: linear-gradient(
        90deg,
        rgb(65, 171, 196) 0%,
        rgb(119, 214, 241) 100%
    );
    border-color: rgb(65, 171, 196) !important;
    border-radius: 0;
}

:deep(.next-page__click-button .arrow) {
    margin-top: 21px;
    margin-left: 40px;
}

:deep(.previous-page__click-button .arrow) {
    margin-right: 40px;
    margin-top: 21px;
}

.custom-table-header {
    display: flex;
    flex-direction: column;
    width: 100%;
    cursor: pointer;
    user-select: none;
    text-align: left;
}

.custom-table-header span {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
}

.custom-table-header span.active {
    font-weight: 400;
}

:deep(.sortType-icon) {
    display: none !important;
}

.cell-multiline {
    display: flex;
    flex-direction: column;
}

.cell-content-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
}

.text-content {
    flex-grow: 1;
    min-width: 0;
}

.reference-icon {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    cursor: pointer;
}

.sectors-select-container {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.el-button {
    float: right;
    margin-top: -35px;
    background-color: #41abc4;
    border-color: #41abc4;
    border-radius: unset;
    width: 200px;
    font-size: 16px !important;
    font-weight: 700 !important;
}

:deep(.el-dialog__title) {
    position: relative;
    font-size: 1.5rem;
}

.dialog-cancel-text {
    position: absolute;
    top: 5px;
    right: 21px;
    cursor: pointer;
    font-size: 14px;
    color: #909399;
    font-weight: 500;
    z-index: 2000;
    user-select: none;
}

.dialog-cancel-text:hover {
    color: #41abc4;
}

.dialog-cancel-text :deep(.el-icon) {
    top: 3px;
    transform: scale(1.4);
}

.description-content {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 10px;
    font-size: 16px;
}

:deep(.el-select__wrapper) {
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #000 !important;
}

:deep(.el-select-dropdown__item) {
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #000 !important;
}

</style>